import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  on,
  useContext,
  type Accessor,
  type JSX,
  type Setter,
} from "solid-js";
import { useLocation } from "@solidjs/router";
import type { ChatViewContext } from "../api/types";
import type { ProjectId } from "../api/types";

interface ChatViewContextValue {
  viewContext: Accessor<ChatViewContext>;
  setViewContext: Setter<ChatViewContext>;
}

const ChatViewCtx = createContext<ChatViewContextValue>();

const DEFAULT_CONTEXT: ChatViewContext = { type: "general" };

export function ChatViewContextProvider(props: {
  children: JSX.Element;
  projectId?: Accessor<ProjectId | undefined>;
}) {
  const location = useLocation();

  // Page-level override set by individual pages (e.g. BacklogPage)
  const [explicitContext, setExplicitContext] =
    createSignal<ChatViewContext>(DEFAULT_CONTEXT);

  // Route-derived fallback: auto-detect /items/:id pages
  const routeFallback = createMemo<ChatViewContext>(() => {
    const match = location.pathname.match(/^\/items\/([^/]+)$/);
    if (match) return { type: "work_item", workItemId: match[1] };
    return DEFAULT_CONTEXT;
  });

  // Reset explicit context on route change so the new page starts fresh
  createEffect(() => {
    void location.pathname; // track dependency
    setExplicitContext(DEFAULT_CONTEXT);
  });

  // Reset explicit context on project switch so stale item context is cleared
  if (props.projectId) {
    createEffect(
      on(props.projectId, () => setExplicitContext(DEFAULT_CONTEXT), {
        defer: true,
      }),
    );
  }

  // Effective context: explicit override (non-general) takes precedence over route fallback
  const viewContext = createMemo<ChatViewContext>(() => {
    const explicit = explicitContext();
    if (explicit.type !== "general") return explicit;
    return routeFallback();
  });

  return (
    <ChatViewCtx.Provider
      value={{ viewContext, setViewContext: setExplicitContext }}
    >
      {props.children}
    </ChatViewCtx.Provider>
  );
}

export function useChatViewContext(): Accessor<ChatViewContext> {
  const ctx = useContext(ChatViewCtx);
  if (!ctx) {
    throw new Error(
      "useChatViewContext must be used within a ChatViewContextProvider",
    );
  }
  return ctx.viewContext;
}

export function useSetChatViewContext(): Setter<ChatViewContext> {
  const ctx = useContext(ChatViewCtx);
  if (!ctx) {
    throw new Error(
      "useSetChatViewContext must be used within a ChatViewContextProvider",
    );
  }
  return ctx.setViewContext;
}
